import { Suspense, useEffect, useRef } from 'react'
import { Canvas, useThree } from '@react-three/fiber'
import { ContactShadows, Environment, Grid, OrbitControls } from '@react-three/drei'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import * as THREE from 'three'
import { Avatar } from './Avatar'
import { YogaMat } from './YogaMat'
import { ViewportCards } from '../ui/ViewportCards'
import { useAppStore } from '../store'
import { getPreset } from '../camera/presets'
import { setRecordingCanvas } from '../recording/capture'

function CameraController() {
  const controls = useRef<OrbitControlsImpl>(null)
  const presetId = useAppStore((s) => s.cameraPreset)
  const locked = useAppStore((s) => s.controlsLocked)
  const { camera } = useThree()

  useEffect(() => {
    const p = getPreset(presetId)
    const start = camera.position.clone()
    const end = new THREE.Vector3(...p.position)
    const targetStart = controls.current?.target.clone() ?? new THREE.Vector3()
    const targetEnd = new THREE.Vector3(...p.target)
    let t = 0
    let raf = 0
    const animate = () => {
      t = Math.min(1, t + 0.04)
      const k = t * t * (3 - 2 * t)
      camera.position.lerpVectors(start, end, k)
      if (controls.current) {
        controls.current.target.lerpVectors(targetStart, targetEnd, k)
        controls.current.update()
      }
      if (t < 1) raf = requestAnimationFrame(animate)
    }
    raf = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(raf)
  }, [presetId, camera])

  return (
    <OrbitControls
      ref={controls}
      enablePan={!locked}
      enableZoom={!locked}
      enableRotate={!locked}
      minDistance={1.2}
      maxDistance={8}
      target={[0, 0.85, 0]}
    />
  )
}

function CaptureBinder() {
  const { gl } = useThree()
  useEffect(() => { setRecordingCanvas(gl.domElement) }, [gl])
  return null
}

export function Scene() {
  const showGrid = useAppStore((s) => s.showGrid)
  const setModelError = useAppStore((s) => s.setModelError)

  return (
    <div className="viewport">
      <Canvas
        shadows
        dpr={[1, 2]}
        gl={{ preserveDrawingBuffer: true, antialias: true }}
        camera={{ position: [0, 1.0, 3.2], fov: 40, near: 0.1, far: 50 }}
        onCreated={() => setModelError(null)}
      >
        <color attach="background" args={['#f4f7f7']} />
        <ambientLight intensity={0.55} />
        <directionalLight castShadow position={[3, 6, 4]} intensity={1.15} shadow-mapSize={[1024, 1024]} />
        <directionalLight position={[-3, 2, -2]} intensity={0.35} />
        <Suspense fallback={null}>
          <Avatar />
          <Environment preset="apartment" environmentIntensity={0.35} />
        </Suspense>
        <YogaMat />
        {showGrid && (
          <Grid
            position={[0, 0.001, 0]}
            args={[8, 8]}
            cellSize={0.25}
            cellThickness={0.5}
            cellColor="#c5d4d4"
            sectionSize={1}
            sectionThickness={1}
            sectionColor="#9bbbbb"
            fadeDistance={10}
            infiniteGrid
          />
        )}
        <ContactShadows position={[0, 0.01, 0]} opacity={0.35} scale={6} blur={2.5} />
        <CameraController />
        <CaptureBinder />
      </Canvas>
      <ViewportCards />
    </div>
  )
}
